import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [authNotification, setAuthNotification] = useState(null);

  // Function to get and refresh token
  const getToken = async (forceRefresh = false) => {
    if (!currentUser) return null;
    try {
      const token = await currentUser.getIdToken(forceRefresh);
      setToken(token);
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  };

  // Function to check and update email verification status
  const checkEmailVerification = async (user) => {
    if (!user) return false;
    try {
      await user.reload(); // Force reload user data
      return user.emailVerified;
    } catch (error) {
      console.error('Error checking email verification:', error);
      return false;
    }
  };

  useEffect(() => {
    console.log('Setting up auth state listener');
    let mounted = true;

    // Offline (or on a bad connection) Firebase's first callback can take a
    // long time. The app shell renders nothing until then, which reads as a
    // white screen in the installed app. Stop blocking after 4 s; the
    // callback still applies when it lands.
    const unblock = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 4000);

    const unsubscribe = onAuthStateChanged(auth, async user => {
      console.log('Auth state changed:', user ? 'User logged in' : 'No user');
      
      if (!mounted) return;
      setLoading(true);
      
      try {
        if (user) {
          // Reload only while unverified: verification never flips back, and
          // the reload is a network round trip that blocks the first render.
          if (!user.emailVerified) {
            await user.reload();
          }
          const isVerified = user.emailVerified;
          console.log('Email verification status:', isVerified);
          
          if (mounted) {
            setCurrentUser(user);
            setIsEmailVerified(isVerified);
            // Get token after confirming verification
            if (isVerified) {
              await getToken();
            }
          }
        } else {
          if (mounted) {
            setCurrentUser(null);
            setToken(null);
            setIsEmailVerified(false);
          }
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      clearTimeout(unblock);
      unsubscribe();
    };
  }, []);

  // Add periodic verification check for unverified users
  useEffect(() => {
    if (currentUser && !isEmailVerified) {
      let mounted = true;
      
      const checkVerification = async () => {
        try {
          await currentUser.reload();
          if (!mounted) return;
          
          const isVerified = currentUser.emailVerified;
          if (isVerified) {
            console.log('Email verified during check');
            setIsEmailVerified(true);
            await getToken(true); // Force token refresh
            // Force page reload to ensure clean state after verification
            window.location.reload();
          }
        } catch (error) {
          console.error('Error checking verification:', error);
        }
      };

      // Check immediately
      checkVerification();
      
      // Then check frequently
      const checkInterval = setInterval(checkVerification, 1000); // Check every second

      return () => {
        mounted = false;
        clearInterval(checkInterval);
      };
    }
  }, [currentUser, isEmailVerified]);

  const logout = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      setCurrentUser(null);
      setToken(null);
      setIsEmailVerified(false);
      // Do not clear all localStorage on logout; it wipes UX preferences such as
      // completed tutorials and player settings.
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.error('Error signing out:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Function to set auth notification
  const showNotification = (message, type = 'info') => {
    setAuthNotification({ message, type });
    
    // Auto-clear notification after 10 seconds
    setTimeout(() => {
      setAuthNotification(null);
    }, 10000);
  };
  
  const value = {
    currentUser,
    loading,
    token,
    isEmailVerified,
    authNotification,
    getToken,
    logout,
    showNotification
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
